<template>
  <div
    class="trial-timeline-with-reminder-step"
    v-if="plan && plan.trialDays">
    <ul class="trial-timeline-with-reminder-step__timeline">
      <li class="trial-timeline-with-reminder-step__date">
        <div class="trial-timeline-with-reminder-step__axis">
          <div class="trial-timeline-with-reminder-step__bullet" />
          <div class="trial-timeline-with-reminder-step__line" />
        </div>
        <h3
          class="trial-timeline-with-reminder-step__label"
          v-html="$t('today')" />
        <p
          class="trial-timeline-with-reminder-step__text"
          v-html="trialStartCopy" />
      </li>

      <li
        class="trial-timeline-with-reminder-step__date">
        <div class="trial-timeline-with-reminder-step__axis">
          <div class="trial-timeline-with-reminder-step__bullet trial-timeline-with-reminder-step__reminder" />
          <div class="trial-timeline-with-reminder-step__line" />
        </div>
        <h3
          class="trial-timeline-with-reminder-step__label"
          v-html="trialReminderDate" />
        <p
          class="trial-timeline-with-reminder-step__text"
          v-html="$t('trialReminderCopy')" />
      </li>

      <li class="trial-timeline-with-reminder-step__date">
        <div class="trial-timeline-with-reminder-step__axis">
          <div class="trial-timeline-with-reminder-step__bullet" />
          <div class="trial-timeline-with-reminder-step__line" />
        </div>
        <h3
          class="trial-timeline-with-reminder-step__label"
          v-html="trialEndDate" />
        <p
          class="trial-timeline-with-reminder-step__text"
          v-html="$t('trialEnd')" />
      </li>
    </ul>
  </div>
</template>

<script>
const messages = {
  en: {
    daysCount: " | one day | {count} days",
    monthsCount: " | one month | {count} months",
    trialStart: "Start enjoying the entire library of Blinks and shows.",
    trialEnd: "Free trial ends.",
    today: "Today",
    trialReminderCopy: "We’ll send you a <strong>reminder</strong> that your trial is ending.",
    arrowMessage: {
      monthly: "Continue learning <br>with Blinkist!",
      yearly: "Continue learning <br>with Blinkist!"
    }
  },
  de: {
    daysCount: " | 1 Tag | {count} Tage",
    monthsCount: " | 1 Monat | {count} Monate",
    trialStart: "Du hast jetzt {trialLength} kostenlos Zugang zu allen Titeln.",
    trialEnd: "Ende des Probeabos",
    today: "Heute",
    trialReminderCopy: "Du erhältst eine <strong>Erinnerung</strong>.",
    arrowMessage: {
      monthly: "Dein Monatsabo <br>beginnt.",
      yearly: "Dein Jahresabo <br>beginnt."
    }
  }
};

const dateTimeFormats = {
  en: {
    dayOfMonth: {
      month: "short",
      day: "numeric"
    }
  },
  de: {
    dayOfMonth: {
      month: "long",
      day: "numeric"
    }
  }
};

export default {
  name: "TrialTimeline",
  props: {
    plan: {
      type: Object,
      default: null,
      validator: function (plan) {
        return plan && typeof plan.trialDays === "number" &&
          typeof plan.type === "string";
      }
    }
  },
  data() {
    return {
      trialStartDate: new Date()
    };
  },
  computed: {
    trialReminderDate() {
      const timestamp = new Date();
      timestamp.setDate(this.trialStartDate.getDate() + (this.plan.trialDays - 2));
      return this.$d(timestamp, "dayOfMonth");
    },
    trialEndDate() {
      const timestamp = new Date();
      timestamp.setDate(this.trialStartDate.getDate() + this.plan.trialDays);
      return this.$d(timestamp, "dayOfMonth");
    },
    trialStartCopy() {
      let trialLength;

      if (this.plan.trialDays % 31 === 0) {
        trialLength = this.$tc("monthsCount", this.plan.trialDays / 31);
      } else {
        trialLength = this.$tc("daysCount", this.plan.trialDays);
      }

      return this.$t("trialStart", { trialLength });
    }
  },
  i18n: {
    dateTimeFormats,
    messages
  }
};
</script>

<style lang="scss">
.trial-timeline-with-reminder-step {
  $root: &;
  $margin-bottom-date: $spacing-24;
  $margin-top-bullet: $spacing-4;
  $size-bullet: $spacing-16;

  padding: 0 0 $spacing-24 $spacing-16;

  background-color: $white;

  &__timeline {
    @include media(min-width, m) {
      display: flex;
      flex-grow: 1;
      margin-bottom: 0;
    }
  }

  &__date {
    position: relative;

    display: grid;
    grid-template-columns: $size-bullet + $spacing-16 auto;
    margin-bottom: $margin-bottom-date;

    &:last-child {
      margin: 0;
      padding-bottom: 1.5rem; /* make last arrow longer */

      #{$root} {

        &__line {
          bottom: 0;

          width: 2px;

          background-color: $blue5;

          /* stylelint-disable unit-blacklist */
          &:before,
          &:after {
            content: "";

            position: absolute;
            right: -1px;
            bottom: 1px;

            width: spacing(11px);
            height: spacing(2px);

            background-color: $blue5;

            border-radius: spacing(2px);

            transform: rotate(45deg);

            @include media(min-width, m) {
              right: -2px; /* added */
              bottom: 3px; /* added */
            }
          }

          &:after {
            right: auto;
            left: -1px;

            transform: rotate(-45deg);

            @include media(min-width, m) {
              top: 3px; /* added */
              right: -2px; /* added */
              left: auto; /* added */
            }
          }
          /* stylelint-enable */

          @include media(min-width, m) {
            width: 100%; /* added */
            height: 2px; /* added */
          }
        }
      }

      @include media(min-width, m) {
        flex-grow: 1;

        margin-bottom: 0;
        padding-right: $spacing-16;
        padding-bottom: 0;
      }
    }

    @include media(min-width, m) {
      display: inline;
      flex-grow: 2;

      margin-bottom: 0;
    }
  }

  &__axis {
    margin-bottom: $spacing-4;
  }

  &__bullet {
    position: relative; /* needed to make z-index work */
    z-index: 2;

    display: block;
    flex-shrink: 0;
    width: $size-bullet;
    height: $size-bullet;
    margin-top: $margin-top-bullet;
    margin-right: $spacing-16;

    background-color: $blue;

    border-radius: 100%;
  }

  &__line {
    position: absolute;
    top: $margin-top-bullet + $size-bullet / 2;
    bottom: - ($margin-bottom-date + $margin-top-bullet + $size-bullet / 2);
    left: $spacing-8;
    z-index: 1;

    display: block;
    width: 4px;

    background-color: $blue;

    transform: translateX(-50%);

    @include media(min-width, m) {
      position: absolute;
      left: 0;

      width: 100%;
      height: 4px;

      transform: translateY(-50%);
    }
  }

  &__label {
    @include font-style(h4, $color: $blue);

    margin: 0;

    @include media(min-width, m) {
      position: absolute;
      top: -2.25rem;
      left: 0;

      font-size: $basex3;
    }
  }

  &__text {
    max-width: 245px;

    margin: 0;

    grid-column-start: 2;

    @include media(min-width, m) {
      max-width: 220px;
    }

    @include media(min-width, l) {
      max-width: 240px;
    }
  }

  &__reminder {
    background-image: url("~@images/shared/bell.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: auto;
  }

  @include media(min-width, m) {
    display: flex;
    margin-bottom: $spacing-48;
    padding: 0;
  }
}
</style>
